import React, { useState } from 'react';
import { Box, TextField, MenuItem, Typography, Chip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';

interface Props {
  status: string;
  type: string;
  startDate: DateTime;
  endDate: DateTime;
  onStatusChange: (status: string) => void;
  onStartDateChange: (date: DateTime) => void;
  onEndDateChange: (date: DateTime) => void;
  onTypeChange: (type: string) => void;
  showDuplicates: boolean;
  onShowDuplicatesChange: (show: boolean) => void;
  startDateLabel?: string;
  endDateLabel?: string;
  enableMaxDateValidation?: boolean;
}

const Filters = ({
  status,
  type,
  startDate,
  endDate,
  onStatusChange,
  onStartDateChange,
  onEndDateChange,
  onTypeChange,
  showDuplicates,
  onShowDuplicatesChange,
  startDateLabel = 'Data inicial',
  endDateLabel = 'Data final',
  enableMaxDateValidation = true,
}: Props) => {
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

  return (
    <Box display="flex" sx={{ flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        {['Últimos 7 dias', 'Últimos 30 dias', 'Mês atual'].map((label) => (
          <Chip
            key={label}
            color="primary"
            variant={selectedFilter === label ? 'filled' : 'outlined'}
            label={label}
            onClick={() => {
              if (label === 'Últimos 7 dias') {
                onStartDateChange(DateTime.now().minus({ days: 7 }));
                onEndDateChange(DateTime.now());
                setSelectedFilter('Últimos 7 dias');
              } else if (label === 'Últimos 30 dias') {
                onStartDateChange(DateTime.now().minus({ days: 30 }));
                onEndDateChange(DateTime.now());
                setSelectedFilter('Últimos 30 dias');
              } else if (label === 'Mês atual') {
                onStartDateChange(DateTime.now().startOf('month'));
                onEndDateChange(DateTime.now().endOf('month'));
                setSelectedFilter('Mês atual');
              }
            }}
          />
        ))}

        <Chip
          key="Mostrar duplicados"
          color="primary"
          variant={showDuplicates ? 'filled' : 'outlined'}
          label="Mostrar duplicados"
          onClick={() => {
            onShowDuplicatesChange(!showDuplicates);
          }}
        />
      </Box>

      <Box
        mb={2}
        sx={{
          display: 'flex',
          gap: 2,
          width: '100%',
        }}
      >
        <Box width="100%">
          <Typography variant="body2" fontWeight="500" color="#434241" mb={1}>
            {startDateLabel}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="pt-br">
            <DatePicker
              value={startDate}
              onChange={(newValue) => {
                if (newValue) {
                  onStartDateChange(newValue);
                  setSelectedFilter(null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 37,
                      borderRadius: '8px',
                    },
                    '& input': {
                      fontSize: '14px',
                    },
                  }}
                  {...params}
                />
              )}
              maxDate={enableMaxDateValidation ? DateTime.now() : undefined}
            />
          </LocalizationProvider>
        </Box>

        <Box width="100%">
          <Typography variant="body2" fontWeight="500" color="#434241" mb={1}>
            {endDateLabel}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="pt-br">
            <DatePicker
              value={endDate}
              onChange={(newValue) => {
                if (newValue) {
                  onEndDateChange(newValue);
                  setSelectedFilter(null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 37,
                      borderRadius: '8px',
                    },
                    '& input': {
                      fontSize: '14px',
                    },
                  }}
                  {...params}
                />
              )}
              maxDate={enableMaxDateValidation ? DateTime.now() : undefined}
            />
          </LocalizationProvider>
        </Box>

        <Box width="100%">
          <Typography variant="body2" fontWeight="500" color="#434241" mb={1}>
            Tipo
          </Typography>
          <TextField
            select
            size="small"
            fullWidth
            value={type}
            onChange={(e) => onTypeChange(e.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                height: 37,
                borderRadius: '8px',
              },
              '& input': {
                fontSize: '14px',
              },
            }}
          >
            <MenuItem value="Todas">Todos</MenuItem>
            <MenuItem value="Boleto">Boleto</MenuItem>
            <MenuItem value="Pix">Pix</MenuItem>
          </TextField>
        </Box>

        <Box width="100%">
          <Typography variant="body2" fontWeight="500" color="#434241" mb={1}>
            Status
          </Typography>
          <TextField
            select
            size="small"
            fullWidth
            value={status}
            onChange={(e) => onStatusChange(e.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                height: 37,
                borderRadius: '8px',
              },
              '& input': {
                fontSize: '14px',
              },
            }}
          >
            <MenuItem value="Todos">Todos</MenuItem>
            <MenuItem value="PAGO">Pago</MenuItem>
            <MenuItem value="A_PAGAR">A pagar</MenuItem>
            <MenuItem value="PENDENTE">Pendente</MenuItem>
            <MenuItem value="CANCELADO">Cancelado</MenuItem>
            <MenuItem value="ERRO_EMISSAO">Erro de emissão</MenuItem>
            <MenuItem value="EM_ATRASO">Em atraso</MenuItem>
          </TextField>
        </Box>
      </Box>
    </Box>
  );
};

export default Filters;
